import type {
  Module,
  ModuleApiProps,
  ModuleForms,
  ModuleGroup,
  ModuleGroups,
  ModuleMutationResponse,
  Modules,
} from '../../@types/modules';
import { isOfflineEnabled } from '../../db/odin-db-util';
import { transformModules } from '../../utils/data-transformation-utils';
import { api } from './api';
import { recordApi } from './record';
import { viewApi } from './view';

export const moduleApi = api.enhanceEndpoints({ addTagTypes: ['ModuleGroups', 'Modules'] }).injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    moduleGroups: builder.query<Array<ModuleGroup>, void>({
      query: () => `/module-groups/simple`,
      transformResponse: (moduleGroups: ModuleGroups) => moduleGroups.items,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'ModuleGroups' as const, id })), { type: 'ModuleGroups', id: 'LIST' }]
          : [{ type: 'ModuleGroups', id: 'LIST' }],
    }),
    modules: builder.query<Array<Module>, { includeFormList?: boolean }>({
      query: (args) => {
        let modulesUrl = '/modules/simple';
        if (args.includeFormList) {
          modulesUrl += '?includeFormList=true';
        }
        return modulesUrl;
      },
      transformResponse: (modules: Modules) => transformModules(modules),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'ModuleGroups' as const, id })), { type: 'Modules', id: 'LIST' }]
          : [{ type: 'Modules', id: 'LIST' }],
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        if (!args.includeFormList || !isOfflineEnabled) {
          return;
        }

        // trigger the prefetch of related metadata
        const { data } = await queryFulfilled;
        data.forEach((module) => {
          const defaultForm =
            module.forms?.length === 1 ? module.forms[0] : module.forms?.find((form) => form.defaultForm);

          if (defaultForm) {
            // trigger the prefetch of related metadata for default form only
            dispatch(api.util.prefetch('formSettings', defaultForm.id, { force: true }));
            dispatch(api.util.prefetch('formAccessControl', defaultForm.id, { force: true }));
            dispatch(api.util.prefetch('formPermissions', defaultForm.id, { force: true }));
            dispatch(
              api.util.prefetch(
                'getWorkflowSteps',
                // args need to match component api call to ensure cache hits
                { formId: defaultForm.id, filters: { archived: { value: false, comparison: '=' } } },
                { force: true }
              )
            );

            dispatch(viewApi.util.prefetch('getViewConfigs', { formId: defaultForm.id }, { force: true }));
            dispatch(viewApi.util.prefetch('getFormAddOns', defaultForm.id, { force: true }));

            dispatch(
              recordApi.util.prefetch(
                'getDefaultRecordValues',
                { id: defaultForm.id, keyTypeId: true },
                { force: true }
              )
            );
          }
        });
      },
    }),
    getModuleById: builder.query<Module, ModuleApiProps>({
      query: ({ moduleId }) => `/modules/simple/${moduleId}`,
      transformResponse: (module: ModuleForms) => module.result,
      providesTags: (_result, _error, arg) => [{ type: 'Modules', id: arg.moduleId }],
    }),
    addModule: builder.mutation<ModuleMutationResponse, Omit<Partial<Module>, 'id'>>({
      query: (body) => ({
        url: '/modules/simple',
        method: 'POST',
        body,
        validateStatus: (response, result) => response.status === 200 && result?.result,
      }),
      invalidatesTags: (result, error) => (error ? [] : [{ type: 'ModuleGroups', id: 'LIST' }]),
    }),
    deleteModule: builder.mutation<ModuleMutationResponse, number>({
      query: (id) => ({
        url: `/modules/${id}`,
        method: 'DELETE',
        validateStatus: (response, result) => response.status === 200 && result?.result,
      }),
      invalidatesTags: (result, error) =>
        error
          ? []
          : [
              { type: 'Modules', id: 'LIST' },
              { type: 'ModuleGroups', id: 'LIST' },
            ],
    }),
    updateModule: builder.mutation<ModuleMutationResponse, Pick<Module, 'id'> & Omit<Partial<Module>, 'id'>>({
      query: ({ id, ...patch }) => ({
        url: `/modules/simple/${id}`,
        method: 'PATCH',
        body: patch,
        validateStatus: (response, result) => response.status === 200 && result?.result,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: 'Modules', id: arg.id },
              { type: 'ModuleGroups', id: 'LIST' },
            ],
    }),
  }),
});

export const {
  useModuleGroupsQuery,
  useModulesQuery,
  useLazyModulesQuery,
  useGetModuleByIdQuery,
  useLazyGetModuleByIdQuery,
  useAddModuleMutation,
  useDeleteModuleMutation,
  useUpdateModuleMutation,
} = moduleApi;
